import React from "react";
import { useMeasure } from "../cyber/charts";

/**
 * Chargeback visualisations.
 *
 * Both charts exist to answer a question a table cannot. The calendar answers
 * "when can we actually cut this?", because idle spend is only recoverable at
 * renewal and a waste figure with no date attached is a complaint rather than a
 * plan. The quadrant answers "which of these is worth the argument?", which
 * needs cost and utilisation on screen together — sorting by either alone hides
 * exactly the products that matter.
 */

const money = (v) => `$${Math.round(v ?? 0).toLocaleString()}`;
const short = (v) =>
  v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${Math.round(v / 1e3)}k` : `$${v}`;

function Tip({ x, y, width, children }) {
  if (!children) return null;
  const flip = x > width - 210;
  return (
    <foreignObject x={flip ? x - 214 : x + 12} y={Math.max(0, y - 10)}
                   width={205} height={150} style={{ pointerEvents: "none" }}>
      <div style={{ background: "rgba(15,23,42,.96)", color: "var(--cares-text-max)", padding: "8px 10px",
                    borderRadius: 8, fontSize: 10.5, lineHeight: 1.5,
                    boxShadow: "0 6px 18px rgba(0,0,0,.3)" }}>
        {children}
      </div>
    </foreignObject>
  );
}

/* ======================================================= renewal calendar */
export function RenewalTimeline({ renewals }) {
  const [ref, width] = useMeasure();
  const [hover, setHover] = React.useState(null);

  const rows = React.useMemo(
    () => [...(renewals || [])].sort((a, b) => (a.daysOut ?? 0) - (b.daysOut ?? 0)),
    [renewals]);

  const PAD = { t: 30, r: 18, b: 30, l: 168 };
  const rowH = 22;
  const H = PAD.t + PAD.b + Math.max(1, rows.length) * rowH;
  const iw = Math.max(180, width - PAD.l - PAD.r);
  const maxDays = Math.max(...rows.map((r) => r.daysOut || 0), 365);
  const x = (d) => PAD.l + (Math.max(0, d) / maxDays) * iw;
  const maxIdle = Math.max(...rows.map((r) => r.idleAnnual || 0), 1);
  const rad = (v) => 4 + Math.sqrt(v / maxIdle) * 11;

  // Month gridlines rather than arbitrary tick counts — renewals are worked to a
  // calendar, not to a percentage of a horizon.
  const ticks = [];
  for (let m = 0; m <= 12; m += 2) {
    const d = m * 30.4;
    if (d <= maxDays) ticks.push({ d, label: m === 0 ? "today" : `+${m}mo` });
  }

  return (
    <div ref={ref}>
      <svg width={width} height={H} style={{ display: "block", overflow: "visible" }}>
        {/* The next 30 days: the only window where action is still possible on
            the imminent renewals. */}
        <rect x={PAD.l} y={PAD.t - 10} width={x(30) - PAD.l} height={H - PAD.t - PAD.b + 14}
              fill="color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))" />
        <text x={PAD.l + 6} y={PAD.t - 15} style={{ fontSize: 9.5, fontWeight: 700, fill: "var(--cares-orange)" }}>
          next 30 days
        </text>

        {ticks.map((t) => (
          <g key={t.label}>
            <line x1={x(t.d)} x2={x(t.d)} y1={PAD.t - 10} y2={H - PAD.b}
                  stroke={t.d === 0 ? "var(--cares-line)" : "var(--cares-line-card)"} strokeWidth={t.d === 0 ? 1.5 : 1} />
            <text x={x(t.d)} y={H - PAD.b + 14} textAnchor="middle"
                  style={{ fontSize: 9, fill: "var(--cares-surface-raised)" }}>{t.label}</text>
          </g>
        ))}

        {rows.map((r, i) => {
          const cy = PAD.t + i * rowH + rowH / 2;
          const imminent = (r.daysOut ?? 999) <= 30;
          const colour = imminent ? "#dc2626" : (r.daysOut ?? 999) <= 90 ? "#ea580c" : "#6366f1";
          const on = hover?.productId === r.productId;
          return (
            <g key={r.productId} onMouseEnter={() => setHover(r)} onMouseLeave={() => setHover(null)}
               style={{ cursor: "pointer" }}>
              <rect x={0} y={cy - rowH / 2} width={width} height={rowH}
                    fill={on ? "var(--cares-surface-board)" : "transparent"} />
              <text x={PAD.l - 9} y={cy + 3} textAnchor="end"
                    style={{ fontSize: 10, fill: on ? "var(--cares-text-1)" : "var(--cares-text-3)",
                             fontWeight: imminent ? 700 : 400 }}>
                {r.product.length > 25 ? `${r.product.slice(0, 24)}…` : r.product}
              </text>
              <line x1={PAD.l} x2={x(r.daysOut || 0)} y1={cy} y2={cy}
                    stroke={colour} strokeOpacity={0.22} strokeWidth={1.5} />
              <circle cx={x(r.daysOut || 0)} cy={cy} r={rad(r.idleAnnual)}
                      fill={colour} fillOpacity={on ? 0.85 : 0.55}
                      stroke={colour} strokeWidth={on ? 2 : 1} />
              {r.idleAnnual > maxIdle * 0.35 && (
                <text x={x(r.daysOut || 0) + rad(r.idleAnnual) + 5} y={cy + 3}
                      style={{ fontSize: 9.5, fill: colour, fontWeight: 700 }}>
                  {short(r.idleAnnual)}
                </text>
              )}
            </g>
          );
        })}

        {hover && (
          <Tip x={x(hover.daysOut || 0)} y={PAD.t} width={width}>
            <div style={{ fontWeight: 700 }}>{hover.product}</div>
            <div style={{ color: "var(--cares-text-dim)" }}>{hover.manufacturer}</div>
            <div style={{ marginTop: 4 }}>
              Renews {hover.renewalDate} · {hover.daysOut} days
            </div>
            <div style={{ color: "var(--cares-orange)", fontWeight: 700 }}>
              {money(hover.idleAnnual)} idle
            </div>
            {hover.flags?.map((f) => (
              <div key={f} style={{ color: "var(--cares-amber)", fontSize: 10 }}>• {f}</div>
            ))}
          </Tip>
        )}
      </svg>
      <div style={{ display: "flex", gap: 14, fontSize: 10.5, color: "var(--cares-text-label)", marginTop: 4 }}>
        <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 9,
                             background: "var(--cares-orange)", marginRight: 4 }} />within 30 days</span>
        <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 9,
                             background: "var(--cares-orange)", marginRight: 4 }} />within 90</span>
        <span>bubble size is idle spend</span>
      </div>
    </div>
  );
}

/* ========================================================= cost vs usage */
export function UtilisationQuadrant({ products }) {
  const [ref, width] = useMeasure();
  const [hover, setHover] = React.useState(null);
  const rows = (products || []).filter((p) => p.utilizationPct != null && p.annualCost > 0);

  const H = 360, PAD = { t: 16, r: 18, b: 42, l: 62 };
  const iw = Math.max(200, width - PAD.l - PAD.r), ih = H - PAD.t - PAD.b;
  const maxCost = Math.max(...rows.map((p) => p.annualCost), 1);

  const x = (u) => PAD.l + (Math.min(100, Math.max(0, u)) / 100) * iw;
  const y = (c) => PAD.t + ih - (c / maxCost) * ih;
  const maxIdle = Math.max(...rows.map((p) => p.idleAnnual || 0), 1);
  const rad = (v) => 4 + Math.sqrt((v || 0) / maxIdle) * 13;

  const xMid = x(50), yMid = y(maxCost * 0.35);

  return (
    <div ref={ref}>
      <svg width={width} height={H} style={{ display: "block", overflow: "visible" }}>
        {/* Expensive and barely used. Everything else is a smaller argument. */}
        <rect x={PAD.l} y={PAD.t} width={xMid - PAD.l} height={yMid - PAD.t} fill="color-mix(in srgb, var(--cares-orange) 14%, var(--cares-surface-card))" />
        <text x={PAD.l + 8} y={PAD.t + 14}
              style={{ fontSize: 10, fontWeight: 700, fill: "var(--cares-orange)" }}>
          EXPENSIVE AND UNUSED — act at renewal
        </text>
        <text x={PAD.l + iw - 8} y={PAD.t + 14} textAnchor="end"
              style={{ fontSize: 10, fill: "var(--cares-surface-raised)" }}>expensive, well used</text>

        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const v = maxCost * f;
          return (
            <g key={f}>
              <line x1={PAD.l} x2={PAD.l + iw} y1={y(v)} y2={y(v)} stroke="var(--cares-line)" />
              <text x={PAD.l - 8} y={y(v) + 3} textAnchor="end"
                    style={{ fontSize: 9, fill: "var(--cares-surface-raised)" }}>{short(v)}</text>
            </g>
          );
        })}
        {[0, 25, 50, 75, 100].map((u) => (
          <text key={u} x={x(u)} y={PAD.t + ih + 16} textAnchor="middle"
                style={{ fontSize: 9, fill: "var(--cares-surface-raised)" }}>{u}%</text>
        ))}
        <line x1={xMid} x2={xMid} y1={PAD.t} y2={PAD.t + ih} stroke="var(--cares-line-strong)" strokeDasharray="3 3" />
        <line x1={PAD.l} x2={PAD.l + iw} y1={yMid} y2={yMid} stroke="var(--cares-line-strong)" strokeDasharray="3 3" />

        {[...rows].sort((a, b) => (a.idleAnnual || 0) - (b.idleAnnual || 0)).map((p) => {
          const on = hover?.productId === p.productId;
          return (
            <circle key={p.productId} cx={x(p.utilizationPct)} cy={y(p.annualCost)}
                    r={rad(p.idleAnnual)}
                    fill={p.actNow ? "var(--cares-orange)" : "var(--cares-purple)"}
                    fillOpacity={p.actNow ? 0.72 : 0.3}
                    stroke={p.actNow ? "var(--cares-orange)" : "var(--cares-purple)"} strokeWidth={on ? 2.5 : 1}
                    style={{ cursor: "pointer" }}
                    onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)} />
          );
        })}

        <text x={PAD.l + iw / 2} y={H - 6} textAnchor="middle"
              style={{ fontSize: 10.5, fill: "var(--cares-surface-raised)", fontWeight: 600 }}>
          Licence utilisation
        </text>
        <text transform={`translate(13,${PAD.t + ih / 2}) rotate(-90)`} textAnchor="middle"
              style={{ fontSize: 10.5, fill: "var(--cares-surface-raised)", fontWeight: 600 }}>Annual cost</text>

        {hover && (
          <Tip x={x(hover.utilizationPct)} y={y(hover.annualCost)} width={width}>
            <div style={{ fontWeight: 700 }}>{hover.product}</div>
            <div style={{ color: "var(--cares-text-dim)" }}>{hover.manufacturer}</div>
            <div style={{ marginTop: 4 }}>
              {money(hover.annualCost)} · {hover.utilizationPct}% used
            </div>
            <div style={{ color: "var(--cares-orange)", fontWeight: 700 }}>
              {money(hover.idleAnnual)} idle
            </div>
            {hover.owner && <div style={{ color: "var(--cares-text-dim)" }}>charged to {hover.owner}</div>}
            {hover.daysOut != null && (
              <div style={{ color: "var(--cares-amber)" }}>renews in {hover.daysOut} days</div>
            )}
          </Tip>
        )}
      </svg>
      <div style={{ display: "flex", gap: 14, fontSize: 10.5, color: "var(--cares-text-label)", marginTop: 4 }}>
        <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 9,
                             background: "var(--cares-orange)", marginRight: 4 }} />
          shortlist — costly, under half used, renewing within 120 days</span>
        <span>bubble size is idle spend</span>
      </div>
    </div>
  );
}
